import { DailyLedgersPanel } from './DailyLedgersPanel';
import { ActionKey } from '@pokerroom/types';
import { CashWorkflowPanel } from './cash/CashWorkflowPanel';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page DailySettlementPage
 * @description 每日日結頁（UI-4-3）
 * 確認收支、輸入日結金額、關閉今日帳務
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type {
  DailySessionDto,
  AccountingCashSummary,
  AccountingCloseReadiness,
  AccountingSessionContext,
} from '@pokerroom/types';

export default function DailySettlementPage() {
  const { t } = useTranslation('accounting');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('sessionId');
  const actorId = useAuthStore((s) => s.user?.id);
  const token = useAuthStore((s) => s.accessToken);
  const revision = useAuthStore((s) => s.sessionRevision);
  const canClose = useAuthStore(
    (s) => s.allowedActions?.includes(ActionKey.ACCOUNTING_CLOSE) === true,
  );
  const venueId = sessionVenueKey(token);
  const scope = [actorId, venueId, revision];
  const formIdentity = `${actorId}:${venueId}:${revision}:${selectedId}`;
  const identityRef = useRef(formIdentity);
  identityRef.current = formIdentity;

  const [closingBalance, setClosingBalance] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const sessionQuery = useQuery<DailySessionDto | null>({
    queryKey: ['accounting-active-session', selectedId, ...scope],
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
    queryFn: ({ signal }) =>
      api.get<DailySessionDto | null>(
        selectedId ? `/accounting/sessions/${selectedId}` : '/accounting/sessions/active',
        { signal },
      ),
  });
  const session = !sessionQuery.isError ? sessionQuery.data : undefined;
  const dayContext = useQuery<AccountingSessionContext>({
    queryKey: ['accounting-session-context', ...scope],
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
    queryFn: ({ signal }) => api.get('/accounting/session-context', { signal }),
    enabled: !!actorId && !!token,
    refetchOnWindowFocus: 'always',
  });
  const isCurrentBusinessDate =
    !dayContext.isError &&
    dayContext.data?.currentBusinessDate === session?.sessionDate.slice(0, 10);
  // Keep the same form mounted while refetching, but never treat cached context as write-ready.
  const parentReady =
    !!session &&
    !!dayContext.data &&
    !sessionQuery.isError &&
    !sessionQuery.isFetching &&
    !dayContext.isError &&
    !dayContext.isFetching &&
    (!selectedId || selectedId === session.id) &&
    session.status === 'OPEN';
  const parentContextKey = JSON.stringify([
    session?.status,
    session?.sessionDate,
    dayContext.data?.currentBusinessDate,
  ]);

  useEffect(() => {
    if (selectedId || !session?.id || session.cashMode !== 'LOCATION_V1') return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('sessionId', session.id);
        return next;
      },
      { replace: true },
    );
  }, [selectedId, session?.id, session?.cashMode, setParams]);

  const summaryQuery = useQuery<AccountingCashSummary>({
    queryKey: ['accounting-summary', session?.id, ...scope],
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
    queryFn: ({ signal }) => api.get(`/accounting/sessions/${session!.id}/summary`, { signal }),
    enabled: !!session?.id && session.cashMode !== 'LOCATION_V1',
  });

  const summary = !summaryQuery.isError ? summaryQuery.data : undefined;
  const summaryError = summaryQuery.isError;
  useEffect(() => {
    setClosingBalance('');
    setConfirmed(false);
  }, [selectedId, session?.id, actorId, venueId, revision]);
  const readiness = useQuery<AccountingCloseReadiness>({
    queryKey: ['accounting-close-readiness', session?.id, ...scope],
    staleTime: 0,
    retry: false,
    queryFn: ({ signal }) => api.get(`/accounting/sessions/${session!.id}/readiness`, { signal }),
    enabled: !!session?.id,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
  });
  const closeMutation = useMutation({
    mutationFn: (intent: { id: string; amount: string; identity: string }) =>
      api.post(`/accounting/sessions/${intent.id}/close`, { closingBalance: intent.amount }),
    onSuccess: (_result, intent) => {
      if (identityRef.current !== intent.identity) return;
      qc.invalidateQueries({ queryKey: ['accounting-active-session'] });
      qc.invalidateQueries({ queryKey: ['accounting-session-context'] });
      navigate(`/accounting/report?sessionId=${intent.id}`);
    },
    onError: (err) => {
      console.warn('[DailySettlement] close failed:', (err as Error).message);
    },
  });

  useEffect(() => {
    setConfirmed(false);
  }, [readiness.dataUpdatedAt, summaryQuery.dataUpdatedAt, parentContextKey]);

  if (sessionQuery.isError || dayContext.isError)
    return (
      <AdminPage header={<AdminPageHeader title={t('pageTitles.settle')} />}>
        <p role="alert">
          {t('cashSummaryFailed')}{' '}
          <button
            type="button"
            onClick={() => {
              void sessionQuery.refetch();
              void dayContext.refetch();
            }}
          >
            {t('dailyLedgers.refresh')}
          </button>
        </p>
      </AdminPage>
    );
  if (sessionQuery.data === undefined || dayContext.isLoading)
    return (
      <AdminPage header={<AdminPageHeader title={t('pageTitles.settle')} />}>
        {' '}
        <p role="status">{t('cashSummaryLoading')}</p>{' '}
      </AdminPage>
    );
  if (!session) {
    return (
      <AdminPage
        className="accounting-page"
        contentClassName="accounting-form-body"
        header={<AdminPageHeader title={t('pageTitles.settle')} />}
      >
        <div className="admin-panel space-y-3">
          <h2 className="text-xl font-bold text-base-content mb-2">{t('noActiveSession')}</h2>
          <a
            href="/accounting/open"
            className="px-6 py-2 rounded-md bg-primary text-primary-content"
          >
            {t('goToOpen')}
          </a>
        </div>
      </AdminPage>
    );
  }

  if (session.cashMode === 'LOCATION_V1')
    return (
      <AdminPage
        className="accounting-page"
        contentClassName="accounting-form-body space-y-6"
        header={
          <AdminPageHeader title={t('pageTitles.settle')} description={t('cashWorkflow.title')} />
        }
      >
        <CashWorkflowPanel
          surface="settlement"
          accountingLayout
          key={`${formIdentity}:${session.id}`}
          sessionId={session.id}
          parentReady={parentReady}
          parentRefreshing={sessionQuery.isFetching || dayContext.isFetching}
          parentContextKey={parentContextKey}
        />
        <DailyLedgersPanel sessionId={session.id} isCurrentBusinessDate={isCurrentBusinessDate} />
      </AdminPage>
    );

  if (!summary)
    return (
      <AdminPage
        className="accounting-page"
        contentClassName="accounting-form-body"
        header={<AdminPageHeader title={t('pageTitles.settle')} />}
      >
        <p role={summaryError ? 'alert' : 'status'}>
          {t(summaryError ? 'cashSummaryFailed' : 'cashSummaryLoading')}
        </p>
        {summaryError && (
          <button type="button" onClick={() => void summaryQuery.refetch()}>
            {t('dailyLedgers.refresh')}
          </button>
        )}
      </AdminPage>
    );
  const totalIncome = Number(summary.totalIncome),
    totalExpense = Number(summary.totalExpense);
  const theoreticalBalance = Number(summary.expectedBalance);
  const canSubmitLegacy =
    parentReady &&
    !summaryQuery.isError &&
    !summaryQuery.isFetching &&
    canClose &&
    !!closingBalance &&
    confirmed &&
    !closeMutation.isPending &&
    !readiness.isError &&
    readiness.data?.ready === true &&
    !readiness.isFetching;

  return (
    <AdminPage
      className="accounting-page"
      contentClassName="accounting-form-body"
      header={<AdminPageHeader title={t('pageTitles.settle')} description={t('closeSession')} />}
    >
      {(sessionQuery.isFetching || dayContext.isFetching || summaryQuery.isFetching) && (
        <p role="status">{t('cashSummaryLoading')}</p>
      )}
      <DailyLedgersPanel sessionId={session.id} isCurrentBusinessDate={isCurrentBusinessDate} />

      {/* 帳務摘要 */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6 space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('sessionDate')}</span>
          <span className="font-medium">{session.sessionDate.slice(0, 10)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('openingBalance')}</span>
          <span className="font-mono">
            NT${parseFloat(session.openingBalance).toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between text-emerald-600">
          <span>+ {t('totalIncome')}</span>
          <span className="font-mono">NT${totalIncome.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-red-600">
          <span>- {t('totalExpense')}</span>
          <span className="font-mono">NT${totalExpense.toLocaleString()}</span>
        </div>
        <hr className="border-base-300" />
        <div className="flex justify-between font-medium text-base">
          <span>{t('theoreticalBalance')}</span>
          <span className="font-mono text-primary">NT${theoreticalBalance.toLocaleString()}</span>
        </div>
      </div>

      <p className="mb-2 text-sm">
        {t('currentBusinessDay', { date: summary.currentBusinessDate })}
      </p>
      <p className="mb-4 text-sm">
        {t('dayBases', {
          accounting: summary.accountingBusinessDate,
          zipa: summary.zipaBusinessDate,
          hour: String(summary.zipaCutoffHour).padStart(2, '0'),
        })}
      </p>
      <p className="mb-4 text-sm">
        {t('noncashTotals', { income: summary.nonCashIncome, expense: summary.nonCashExpense })}
      </p>
      {session.isCurrentBusinessDate === false && <p role="alert">{t('historicalOpenSession')}</p>}
      {/* 實際日結金額 */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6">
        <label className="block text-sm text-base-content/70 mb-2">
          {t('closingBalance')} (NT$) *
        </label>
        <input
          type="number"
          value={closingBalance}
          onChange={(e) => setClosingBalance(e.target.value)}
          placeholder={t('suggest', { value: theoreticalBalance.toLocaleString() })}
          className="w-full px-3 py-3 rounded-md border border-base-300 bg-base-200 text-base-content text-xl
                     font-mono focus:outline-hidden focus:ring-2 focus:ring-primary"
        />
        {closingBalance && Math.abs(parseFloat(closingBalance) - theoreticalBalance) > 0.01 && (
          <p
            className={`text-xs mt-1.5 ${parseFloat(closingBalance) > theoreticalBalance ? 'text-emerald-600' : 'text-red-600'}`}
          >
            {t('difference')}
            {parseFloat(closingBalance) > theoreticalBalance ? '+' : ''}
            NT${(parseFloat(closingBalance) - theoreticalBalance).toLocaleString()}
          </p>
        )}
      </div>

      {/* 確認勾選 */}
      <label className="flex items-center gap-2 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="w-4 h-4 text-primary rounded border-base-300"
        />
        <span className="text-sm text-base-content">{t('confirmSettleText')}</span>
      </label>

      {readiness.isError && (
        <p role="alert">
          {t('closeReadinessFailed')}{' '}
          <button type="button" onClick={() => void readiness.refetch()}>
            {t('dailyLedgers.refresh')}
          </button>
        </p>
      )}
      {readiness.data && !readiness.data.ready && (
        <section className="mb-4 border border-warning p-3" aria-label={t('closeBlockers')}>
          <p>{t('closeBlockerCount', { count: readiness.data.count })}</p>
          <ul>
            {readiness.data.blockers.map((event) => (
              <li key={event.id}>
                <Link
                  className="link min-h-11 inline-flex items-center"
                  to={`/tournament/${event.id}`}
                >
                  {event.name}
                </Link>
              </li>
            ))}
          </ul>
          {readiness.data.unresolved.length > 0 && (
            <>
              <p>{t('historicalDayUnresolved')}</p>
              <ul>
                {readiness.data.unresolved.map((event) => (
                  <li key={event.id}>
                    <Link
                      className="link min-h-11 inline-flex items-center"
                      to={`/tournament/${event.id}`}
                    >
                      {event.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
      {!closingBalance && <p role="status">{t('enterClosingBalance')}</p>}
      {closingBalance && !confirmed && <p role="status">{t('confirmBeforeClose')}</p>}
      {closeMutation.error && (
        <div className="mb-4 px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
          {(closeMutation.error as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? t('closeFailed', { defaultValue: 'Failed to close session' })}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => navigate('/accounting/expenses')}
          className="flex-1 py-2.5 rounded-md border border-base-300 text-sm"
        >
          {tc('back')}
        </button>
        <button
          onClick={() => {
            const url = `/api/accounting/sessions/${session.id}/pdf`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          className="py-2.5 px-4 rounded-md border border-base-300 text-sm hover:bg-base-300 transition-colors"
        >
          {t('downloadPdf', { defaultValue: 'PDF' })}
        </button>
        <button
          aria-label={t('confirmSettle')}
          aria-busy={closeMutation.isPending}
          onClick={() => {
            if (!canSubmitLegacy) return;
            closeMutation.mutate({
              id: session.id,
              amount: closingBalance,
              identity: formIdentity,
            });
          }}
          disabled={!canSubmitLegacy}
          className="flex-1 py-2.5 rounded-md bg-primary text-primary-content text-sm font-medium disabled:opacity-50"
        >
          {closeMutation.isPending ? tc('loading') : t('confirmSettle')}
        </button>
      </div>
    </AdminPage>
  );
}
